export const formatAiFeatureLabel = (log) => {
  const type = log?.request_data?.type;
  if (!type) return "AI Call";
  const key = String(type).toLowerCase();
  const labels = {
    hpi: "HPI",
    icd: "ICD-10",
    orders: "Orders",
    discharge: "Discharge",
    work_restrictions: "Work Restrictions",
    mdm_plan: "MDM Plan",
    transcript_note: "Transcript Note",
    transcript_note_stripped: "Transcript Strip (Haiku)",
  };
  if (labels[key]) return labels[key];
  return String(type)
    .replace(/_/g, " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
};

export const mapAiStatusToUiMeta = (status = "") => {
  const normalized = String(status || "").toLowerCase();
  if (normalized === "success") {
    return {
      actionType: "note-added",
      badgeLabel: "Success",
    };
  }
  if (normalized === "error" || normalized === "failed") {
    return {
      actionType: "other",
      badgeIconClass: "fa-solid fa-times-circle me-1",
      badgeLabel: normalized === "failed" ? "Failed" : "Error",
    };
  }
  if (normalized === "pending" || normalized === "processing") {
    return {
      actionType: "field-update",
      badgeIconClass: "fa-solid fa-clock me-1",
      badgeLabel: normalized === "processing" ? "Processing" : "Pending",
    };
  }
  return {
    actionType: "field-update",
    badgeIconClass: "fa-solid fa-robot me-1",
    badgeLabel: status ? String(status) : "Unknown",
  };
};

export const getRelativeDateLabel = (dateValue) => {
  if (!dateValue) return "";
  const now = new Date();
  const date = new Date(dateValue);
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfDate = new Date(date.getFullYear(), date.getMonth(), date.getDate());
  const diffInDays = Math.floor((startOfToday - startOfDate) / 86400000);
  if (diffInDays === 0) return "Today";
  if (diffInDays === 1) return "Yesterday";
  return "";
};

export const getDateRangeForAuditFilter = (filterKey) => {
  const now = new Date();
  const formatMDY = (dateObj) => {
    const mm = String(dateObj.getMonth() + 1).padStart(2, "0");
    const dd = String(dateObj.getDate()).padStart(2, "0");
    const yyyy = dateObj.getFullYear();
    return `${mm}/${dd}/${yyyy}`;
  };
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday.getTime() - 86400000);
  const startOfLast7Days = new Date(startOfToday.getTime() - 6 * 86400000);
  const startOfLast30Days = new Date(startOfToday.getTime() - 29 * 86400000);
  if (filterKey === "today") {
    const d = formatMDY(startOfToday);
    return { date_from: d, date_to: d };
  }
  if (filterKey === "yesterday") {
    const d = formatMDY(startOfYesterday);
    return { date_from: d, date_to: d };
  }
  if (filterKey === "last-7-days") {
    return { date_from: formatMDY(startOfLast7Days), date_to: formatMDY(now) };
  }
  if (filterKey === "last-30-days") {
    return { date_from: formatMDY(startOfLast30Days), date_to: formatMDY(now) };
  }
  return {};
};

export const getPaginationMeta = (page, perPage, totalRecords, totalPages) => {
  const resolvedTotalPages = Math.max(
    1,
    Number(totalPages) > 0
      ? Number(totalPages)
      : Math.ceil((totalRecords || 0) / Math.max(1, perPage || 25)) || 1
  );
  const maxVisible = 5;
  const current = page;
  const half = Math.floor(maxVisible / 2);
  let start = Math.max(1, current - half);
  let end = Math.min(resolvedTotalPages, start + maxVisible - 1);
  start = Math.max(1, end - maxVisible + 1);
  const visiblePages = [];
  for (let i = start; i <= end; i += 1) {
    visiblePages.push(i);
  }
  return { totalPages: resolvedTotalPages, visiblePages };
};

export const mapApiRowToAiAuditEntry = (row, index) => {
  const { actionType, badgeIconClass, badgeLabel } = mapAiStatusToUiMeta(row?.status);
  const timestamp = row?.created_at;
  const dateObj = timestamp ? new Date(timestamp) : null;
  const time = dateObj
    ? dateObj.toLocaleTimeString("en-US", { hour: "2-digit", minute: "2-digit" })
    : "-";
  const date = dateObj
    ? dateObj.toLocaleDateString("en-US", {
        month: "short",
        day: "numeric",
        year: "numeric",
      })
    : "-";
  const rawId = row?.log_id || row?.id || `ai-${index}`;
  const safeId = String(rawId).replace(/[^a-zA-Z0-9_-]/g, "");
  const collapseId = `aiAuditItem-${index + 1}-${safeId}`;
  const requestData = row?.request_data || {};
  const responseData = row?.response_data || {};

  return {
    id: collapseId,
    logId: rawId,
    actionType,
    itemClass: "status-change",
    time,
    date,
    relative: getRelativeDateLabel(timestamp),
    user: formatAiFeatureLabel(row),
    badgeLabel,
    badgeIconClass,
    actionLabel: row?.model_id || "Unknown model",
    inputTokens: row?.input_tokens,
    outputTokens: row?.output_tokens,
    totalTokens: row?.total_tokens,
    responseTimeMs: row?.response_time_ms,
    inputText:
      requestData?.input_text ||
      requestData?.request_text ||
      requestData?.prompt ||
      "",
    outputText:
      responseData?.output_text ||
      responseData?.response_text ||
      responseData?.raw_text ||
      "",
    errorMessage: row?.error_message || "",
    dataSearch: `${formatAiFeatureLabel(row)} ${row?.model_id || ""} ${
      row?.status || ""
    }`.toLowerCase(),
  };
};
